import React, { useCallback, useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { fmtMoney } from "@/lib/format";
import { toast } from "sonner";
import { Plus, Trash2, Edit3 } from "lucide-react";
import RateEditor from "./_shared/RateEditor";

/**
 * Card rendered on the Customer Detail page for managing that customer's
 * customer-specific rate overrides. These override the default service rate
 * for this customer only.
 */
export default function CustomerRatesCard({ customerId, customerName }) {
  const [rates, setRates] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, s] = await Promise.all([
        api.get("/finance/rate-rules", { params: { limit: 100 } }),
        api.get("/service-types", { params: { limit: 100 } }),
      ]);
      setRates((r.data.items || []).filter(
        (rr) => rr.customer_account_id === customerId && rr.is_active,
      ));
      setServices(s.data.items || []);
    } finally { setLoading(false); }
  }, [customerId]);
  useEffect(() => { load(); }, [load]);

  const svcLabel = (id) =>
    services.find((s) => s.id === id)?.label || id?.slice(0, 8) || "—";

  const del = async (id) => {
    if (!window.confirm("Remove this customer-specific rate? Default will apply next time.")) return;
    try {
      await api.delete(`/finance/rate-rules/${id}`);
      toast.success("Removed");
      load();
    } catch (e) { toast.error(formatError(e, "Failed")); }
  };

  return (
    <div className="bg-white border border-slate-200 p-5" data-testid="customer-rates-card">
      <div className="flex items-center justify-between mb-3">
        <div className="label-overline">Customer-specific rates</div>
        <Button onClick={() => { setEditing(null); setShowAdd(true); }}
                data-testid="customer-rate-add-btn"
                className="rounded bg-slate-900 hover:bg-slate-800 h-9 px-3">
          <Plus size={14} className="mr-2"/> Add rate
        </Button>
      </div>
      <p className="text-xs text-slate-600 mb-4">
        These rates override the default service rate for this customer only.
      </p>
      {loading ? (
        <div className="label-overline text-slate-500">Loading…</div>
      ) : rates.length === 0 ? (
        <div className="border-2 border-dashed border-slate-200 rounded p-6 text-center text-xs text-slate-500">
          No customer-specific rates. Default service rates apply.
        </div>
      ) : (
        <table className="tbl w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200 label-overline text-slate-600">
            <tr>
              <th className="text-left px-3 py-2">Code</th>
              <th className="text-left px-3 py-2">Service</th>
              <th className="text-left px-3 py-2">Unit</th>
              <th className="text-right px-3 py-2">Price</th>
              <th className="w-24"></th>
            </tr>
          </thead>
          <tbody>
            {rates.map((r) => (
              <tr key={r.id} className="border-b border-slate-100"
                  data-testid={`customer-rate-row-${r.code}`}>
                <td className="px-3 py-2 font-mono text-xs">{r.code}</td>
                <td className="px-3 py-2">{svcLabel(r.service_type_id)}</td>
                <td className="px-3 py-2 font-mono text-xs">{r.unit || "—"}</td>
                <td className="px-3 py-2 font-mono text-right">{fmtMoney(r.unit_price_cents)}</td>
                <td className="px-3 py-2 flex gap-1 justify-end">
                  <button onClick={() => { setEditing(r); setShowAdd(true); }}
                          data-testid={`customer-rate-edit-${r.code}`}
                          className="p-1 rounded hover:bg-slate-100 text-slate-600">
                    <Edit3 size={14}/>
                  </button>
                  <button onClick={() => del(r.id)}
                          data-testid={`customer-rate-del-${r.code}`}
                          className="p-1 rounded hover:bg-red-50 text-red-600">
                    <Trash2 size={14}/>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <RateEditor open={showAdd} onClose={() => setShowAdd(false)}
                  onSaved={() => { setShowAdd(false); load(); }}
                  services={services}
                  editing={editing}
                  customerAccountId={customerId}
                  customerName={customerName} />
    </div>
  );
}
